"use client";

import Image from "next/image";
import { useRef } from "react";
import { motion } from "framer-motion";
import { useInViewAnimation } from "../animation/useInViewAnimation";
import { useTranslation } from "next-i18next";
import Navbar from "../components/Navbar";
import dynamic from "next/dynamic";

const ScrollFloat = dynamic(() => import("./animations/ScrollFloat"), {
  ssr: false,
});

export default function About() {
  const { t } = useTranslation("about");
  const leftRef = useRef(null);
  const rightRef = useRef(null);

  const leftInView = useInViewAnimation(leftRef);
  const rightInView = useInViewAnimation(rightRef);

  return (
    <div id="about" className="bg-reefliBeige min-h-[90vh] w-full">
      <div className="mx-auto max-w-[1150px] px-4 lg:px-8 pt-8 sm:pt-12 lg:pt-24 pb-8 sm:pb-12 lg:pb-24 font-[futura] text-[#262104]">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <ScrollFloat
            animationDuration={1}
            ease="back.inOut(2)"
            scrollStart="center bottom+=50%"
            scrollEnd="bottom bottom-=40%"
            stagger={0.03}
            containerClassName="inline-block"
            textClassName="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight"
          >
            {t("title")}
          </ScrollFloat>
        </motion.h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-10 lg:gap-y-12 lg:gap-x-16 items-start">
          {/* LEFT */}
          <motion.div
            ref={leftRef}
            initial={{ opacity: 0, y: 50 }}
            animate={leftInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center lg:items-start lg:pr-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={leftInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="
                mt-6 lg:mt-8
                relative w-full max-w-[280px] sm:max-w-[360px] md:max-w-[440px] lg:max-w-[560px] 
                aspect-[4/5] sm:aspect-[1/1] md:aspect-[5/6] lg:aspect-[10/11]
                rounded-2xl overflow-hidden shadow-md mx-auto
                lg:rounded-3xl lg:shadow-lg
              "
            >
              <Image
                src="/img/IMG_9940.JPG"
                alt="Bottles"
                fill
                priority
                sizes="(max-width: 640px) 90vw, (max-width: 1024px) 80vw, 560px"
                className="object-cover object-center"
              />
            </motion.div>

            <p className="mt-6 lg:mt-8 text-base md:text-lg leading-6 sm:leading-7 md:leading-8 text-center lg:text-left px-1 sm:px-0">
              {t("paragraph1")}
            </p>
          </motion.div>

          {/* RIGHT */}
          <motion.div
            ref={rightRef}
            initial={{ opacity: 0, y: 50 }}
            animate={rightInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center lg:items-start lg:pl-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={rightInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="
                mt-6 lg:mt-8
                relative w-full max-w-[280px] sm:max-w-[360px] md:max-w-[440px] lg:max-w-[560px]
                aspect-[4/5] sm:aspect-[1/1] md:aspect-[5/6] lg:aspect-[10/11]
                rounded-2xl overflow-hidden shadow-md mx-auto
                lg:rounded-3xl lg:shadow-lg lg:mx-0
              "
            >
              <Image
                src="/img/katalog/IMG_0868.JPG"
                alt="Bottles"
                fill
                sizes="(max-width: 640px) 90vw, (max-width: 1024px) 80vw, 460px"
                className="object-cover object-center"
              />
            </motion.div>

            <div className="mt-6 lg:mt-8 w-full max-w-[680px] space-y-6 text-center lg:text-left px-1 sm:px-0">
              <p className="text-base md:text-lg leading-6 sm:leading-7 md:leading-8">
                <b>
                  REEFLI
                  <span className="align-super text-[0.65em] font-sans ml-0.5">
                    ™
                  </span>
                </b>{" "}
                {t("paragraph2")}
              </p>
              <div>
                <h3 className="font-semibold text-base sm:text-lg md:text-xl mb-1">
                  {t("missionTitle")}
                </h3>
                <p className="text-base sm:text-base md:text-lg leading-6 sm:leading-7 md:leading-8">
                  {t("missionText")}
                  <br />
                  {t("missionText2")}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Navbar />
    </div>
  );
}
